import { inject, Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable, Subscription } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";

import { Context, WINDOW } from "@hermes/core-app-core";
import { FeatureFlagFacade, RISKIFIED_BEACON } from "@hermes/hermes-web-states-flipper-state";
import { LOGGER } from "@hermes/core-web-logger";

import { LoadRiskifiedBeaconLibraryService } from "./load-riskified-beacon-library.service";

@Injectable()
export class RiskifiedBeaconService implements OnDestroy {
    private router = inject(Router);
    private loadRiskifiedBeaconLibraryService = inject(
        LoadRiskifiedBeaconLibraryService,
    );
    private cookieService = inject(CookieService);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private context = inject(Context);
    private logger = inject(LOGGER);
    private window = inject<
        Window & {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            RISKX: {
                setSid: (argument0: string) => void;
                go: (argument0: string) => void;
            };
        }
    >(WINDOW);

    private subscription: Subscription = new Subscription();
    private sessionId = this.cookieService.get("correlation_id");

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public executeService(): void {
        if (!this.context.isInBrowserMode()) {
            return;
        }
        this.subscription.add(
            this.featureFlagFacade
                .isActivated(RISKIFIED_BEACON)
                .pipe(
                    filter((isActived) => isActived),
                    tap(() =>
                        this.loadRiskifiedBeaconLibraryService.load(
                            this.window.location.hostname,
                            this.sessionId,
                        ),
                    ),
                    switchMap(
                        () => this.loadRiskifiedBeaconLibraryService.isLoaded$,
                    ),
                    filter((isLoaded) => isLoaded),
                    switchMap(() => this.sendClientData()),
                )
                .subscribe(),
        );

        this.subscription.add(
            this.loadRiskifiedBeaconLibraryService.isError$
                .pipe(filter((isError) => isError))
                .subscribe(() => {
                    this.logger.error("Riskified Beacon can't be loaded");
                }),
        );
    }

    private sendClientData(): Observable<void> {
        return this.router.events.pipe(
            filter(
                (event): event is NavigationEnd =>
                    event instanceof NavigationEnd,
            ),
            map((data: NavigationEnd) => {
                const newSessionId = this.cookieService.get("correlation_id");
                if (this.sessionId !== newSessionId) {
                    this.window.RISKX.setSid(newSessionId);
                    this.sessionId = newSessionId;
                }
                this.window.RISKX.go(data.url);
            }),
        );
    }
}
